import { useCallback, useEffect, useState, type FormEvent } from "react";
import { ErrorNotice, Field, useAction } from "../components/ui";
import { api, inviteLink, type Invitation, type Me, type Role, type TeamUser } from "../lib/api";

const dateFmt = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short" });
const pad = (n: number) => String(n).padStart(2, "0");

export function Team({ me }: { me: Me }) {
  const isOwner = me.user.role === "owner";
  const [users, setUsers] = useState<TeamUser[] | null>(null);
  const [invites, setInvites] = useState<Invitation[]>([]);
  const [newLink, setNewLink] = useState<{ email: string; url: string } | null>(null);
  const load = useAction();
  const act = useAction();

  const reload = useCallback(
    () =>
      load.run(async () => {
        const [u, i] = await Promise.all([api.users(), isOwner ? api.invitations() : Promise.resolve({ invitations: [] })]);
        setUsers(u.users);
        setInvites(i.invitations);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOwner],
  );
  useEffect(() => void reload(), [reload]);

  const change = (user: TeamUser, patch: { role?: Role; status?: TeamUser["status"] }) =>
    void act.run(async () => {
      await api.updateUser(user.id, patch);
      await reload();
    });

  const revoke = (inv: Invitation) =>
    void act.run(async () => {
      await api.revokeInvitation(inv.id);
      if (newLink?.email === inv.email) setNewLink(null);
      await reload();
    });

  const activeCount = users?.filter((u) => u.status === "active").length ?? 0;

  return (
    <>
      <div className="page-head">
        <p className="label">{me.tenant.name}</p>
        <h1 className="display">
          Your team, <em>assembled</em>.
        </h1>
        <p className="lede">
          Everyone here sees the same companies, jobs and candidates, so nobody chases a role a colleague is already working.
        </p>
      </div>

      <ErrorNotice error={load.error ?? act.error} />

      <section className="section" aria-labelledby="members-h">
        <div className="section-head">
          <h2 id="members-h" className="label">
            Members
          </h2>
          <span className="label figures">{users ? `${pad(activeCount)} active` : "…"}</span>
        </div>
        <ol className="index">
          {users?.map((u, i) => {
            const self = u.id === me.user.id;
            const active = u.status === "active";
            return (
              <li key={u.id} className={active ? undefined : "inactive"}>
                <span className="num" aria-hidden>
                  {pad(i + 1)}
                </span>
                <span className="main">
                  <span className="primary">
                    {u.displayName ?? u.email}
                    {self && <span className="label"> · you</span>}
                  </span>
                  <span className="secondary">
                    <span className={`status ${active ? "" : "off"}`}>{active ? u.role : "deactivated"}</span>
                    {u.displayName && <span className="figures"> · {u.email}</span>}
                  </span>
                </span>
                <span className="actions">
                  {isOwner && !self && (
                    <>
                      {active && (
                        <button
                          type="button"
                          className="link-btn"
                          disabled={act.pending}
                          onClick={() => change(u, { role: u.role === "owner" ? "consultant" : "owner" })}
                        >
                          Make {u.role === "owner" ? "consultant" : "owner"}
                        </button>
                      )}
                      <button
                        type="button"
                        className={`link-btn ${active ? "danger" : ""}`}
                        disabled={act.pending}
                        onClick={() => change(u, { status: active ? "deactivated" : "active" })}
                      >
                        {active ? "Deactivate" : "Reactivate"}
                      </button>
                    </>
                  )}
                </span>
              </li>
            );
          })}
          {!users && !load.error && (
            <li>
              <span className="empty">Loading…</span>
            </li>
          )}
        </ol>
      </section>

      {isOwner && (
        <section className="section" aria-labelledby="invite-h">
          <div className="section-head">
            <h2 id="invite-h" className="label">
              Invite someone
            </h2>
            <span className="label figures">{pad(invites.length)} pending</span>
          </div>
          <InviteForm
            me={me}
            onCreated={async (email, token) => {
              setNewLink({ email, url: inviteLink(token) });
              await reload();
            }}
          />
          {newLink && <CopyLink email={newLink.email} url={newLink.url} />}
          {invites.length > 0 && (
            <ol className="index">
              {invites.map((inv, i) => (
                <li key={inv.id}>
                  <span className="num" aria-hidden>
                    {pad(i + 1)}
                  </span>
                  <span className="main">
                    <span className="primary">{inv.email}</span>
                    <span className="secondary">
                      <span className="status pending">{inv.role}</span>{" "}
                      <span className="figures">· expires {dateFmt.format(new Date(inv.expiresAt))}</span>
                    </span>
                  </span>
                  <span className="actions">
                    <button type="button" className="link-btn danger" disabled={act.pending} onClick={() => revoke(inv)}>
                      Withdraw
                    </button>
                  </span>
                </li>
              ))}
            </ol>
          )}
        </section>
      )}
    </>
  );
}

function InviteForm({ me, onCreated }: { me: Me; onCreated: (email: string, token: string) => Promise<void> }) {
  const { pending, error, run } = useAction();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("consultant");

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const { invitation, token } = await api.invite(email.trim(), role);
      setEmail("");
      await onCreated(invitation.email, token);
    });
  };

  return (
    <>
      <form className="inline-form" onSubmit={submit}>
        <Field label="Email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        <label className="field">
          <span className="label">Role</span>
          <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
            <option value="consultant">Consultant</option>
            <option value="owner">Owner</option>
          </select>
        </label>
        <button className="btn" disabled={pending}>
          {pending ? "Inviting…" : "Create invite"}
        </button>
      </form>
      <SeatCost me={me} />
      <ErrorNotice error={error} />
    </>
  );
}

/** Paying agencies see what a new colleague costs before inviting them. */
function SeatCost({ me }: { me: Me }) {
  const [text, setText] = useState<string | null>(null);
  const billing = me.tenant.billing;
  const paid = billing.enabled && billing.plan === "paid";
  useEffect(() => {
    if (!paid) return;
    api
      .billing()
      .then((b) => {
        if (!b.enabled || b.plan !== "paid") return;
        const pounds = (p: number) => `£${(p / 100).toLocaleString("en-GB")}`;
        const i = b.interval ?? "month";
        setText(
          `Each person who joins adds ${pounds(b.prices[i].additional)} ${i === "month" ? "a month" : "a year"} to your subscription, from the day they accept. Deactivating someone stops it.`,
        );
      })
      .catch(() => {});
  }, [paid]);
  return text ? <p className="fine-print">{text}</p> : null;
}

/** The one-time link is only ever shown here: the server keeps just its hash. */
function CopyLink({ email, url }: { email: string; url: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
  };
  return (
    <div className="form copy-block">
      <p className="notice ok">
        Send this link to <strong>{email}</strong>. It works once and expires in 7 days. It won't be shown again.
      </p>
      <div className="copy-row">
        <code>{url}</code>
        <button type="button" className="btn secondary" onClick={() => void copy()}>
          {copied ? "Copied" : "Copy link"}
        </button>
      </div>
    </div>
  );
}
